/** @jsxImportSource theme-ui */
import { Container, Flex, Heading, Badge } from 'theme-ui';
import EditDiscountAgreement from '@/containers/dicountAgreements/EditDiscountAgreement';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '@/components/Button';
import { useFormik } from 'formik';
import {
    useEditDiscountAgreementsMutation,
    useGetDiscountAgreementQuery,
} from '@/store/apiService';
import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@/utils/helpers';
import { DiscountAgreementStatusEnum } from '@/types/types';
import { buildStatusChangePayload } from '@/containers/dicountAgreements/discountAgreementStatus';

const DRAFT = 'Draft';
const PUBLISHED = 'Published';
const ARCHIVED = 'Archived';

const EditDiscountAgreementPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { data: discountAgreement } = useGetDiscountAgreementQuery({ id });
    const [editDiscountAgreement, editDiscountAgreementState] = useEditDiscountAgreementsMutation();

    const changeStatus = (nextStatus: DiscountAgreementStatusEnum) => {
        editDiscountAgreement(buildStatusChangePayload(discountAgreement, nextStatus));
    };

    useEffect(() => {
        if (editDiscountAgreementState.isError) {
            toast.error(getErrorMessage(editDiscountAgreementState.error));
        }

        if (editDiscountAgreementState.isSuccess) {
            toast.success(editDiscountAgreementState.data.Status);
        }
    }, [editDiscountAgreementState.isError, editDiscountAgreementState.isSuccess]);

    return (
        <Container>
            {discountAgreement?.Status === ARCHIVED ? <Badge>Archived</Badge> : null}
            <Flex mb="2" sx={{ justifyContent: 'space-between' }}>
                <Heading sx={{ textTransform: 'capitalize' }} as="h2">
                    Edit Discount Agreement
                </Heading>

                <Flex sx={{ gap: 2 }}>
                    {discountAgreement?.Status === PUBLISHED ? (
                        <Button
                            isLoading={editDiscountAgreementState.isLoading}
                            onClick={() => changeStatus(DiscountAgreementStatusEnum.Archived)}
                        >
                            Archive
                        </Button>
                    ) : null}

                    {discountAgreement?.Status === ARCHIVED ? (
                        <Button
                            variant="danger"
                            isLoading={editDiscountAgreementState.isLoading}
                            onClick={() => changeStatus(DiscountAgreementStatusEnum.Discarded)}
                        >
                            Discard
                        </Button>
                    ) : null}

                    {discountAgreement?.Status === DRAFT ? (
                        <Button
                            isLoading={editDiscountAgreementState.isLoading}
                            onClick={() => changeStatus(DiscountAgreementStatusEnum.Published)}
                        >
                            Publish
                        </Button>
                    ) : null}
                </Flex>
            </Flex>
            <EditDiscountAgreement
                discountAgreementId={id}
                onSubmitSuccess={() => navigate('/voss/discount-agreements')}
            />
        </Container>
    );
};

export default EditDiscountAgreementPage;
